import { PositioningPortal } from '@codastic/react-positioning-portal'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import { BsThreeDots, BsThreeDotsVertical } from 'react-icons/bs'

import type { ReactElement, SyntheticEvent, ReactNode, MouseEvent } from 'react'

import LinkButton from '~/components/Buttons/LinkButton'
import Tooltip from '~/components/Tooltip'
import UnauthorizedTooltip from '~/components/UnauthorizedTooltip'
import { useOnClickOutside } from '~/hooks/useOnClickOutside'

import './styles.scss'

export interface MenuItem {
  action: VoidFunction
  className?: string
  label: string
  type?: 'default' | 'external' | 'internal'
}

export interface Menu {
  actions?: MenuItem[]
  navigations?: MenuItem[]
}

export interface MainAction {
  action: VoidFunction
  icon: string
  tooltipOverlay?: ReactNode
}

interface Props {
  className?: string
  disabled?: boolean
  iconDirection?: 'vertical' | 'horizontal'
  mainAction?: MainAction
  menu: Menu
  menuClassName?: string
  menuTitle?: string
  placement?: 'bottom' | 'top'
}

const ActionMenu = ({
  className,
  disabled,
  iconDirection = 'horizontal',
  mainAction,
  menu,
  menuClassName,
  menuTitle = 'Actions',
  placement = 'bottom',
}: Props): ReactElement | null => {
  const { actions, navigations } = menu

  const [isToggled, toggle] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)

  const toggledMenuRef = useRef<HTMLDivElement>(null)

  const menuContainerClasses = classNames('actions-menu-container', className)

  const actionMenuClasses = classNames('actions-menu', {
    'with-main-action': !!mainAction,
  })

  const dropdownMenuClasses = classNames(actionMenuClasses, 'dropdown-menu', iconDirection, {
    'actions-menu--disabled': disabled,
  })

  const mainActionClasses = classNames(actionMenuClasses, 'main-action-menu', {
    'with-dropdown-menu': !!menu.actions || !!menu.navigations,
  })

  const sectionClassname = 'menu-items-section'

  const sectionClasses = classNames(sectionClassname, {
    'with-border': !!menu.navigations,
  })

  const menuItemClasses = classNames('action-menu-menu-items', menuClassName, { [placement]: true })

  useOnClickOutside(toggledMenuRef, () => toggle(false))

  const handleOpenMenu = (value: boolean) => () => toggle(value)

  const handleClick = (evt: SyntheticEvent<HTMLDivElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
  }

  const handleAction = (action: VoidFunction) => (evt: MouseEvent) => {
    evt.preventDefault()
    evt.stopPropagation()
    action()
    toggle(false)
  }

  if (!mainAction && !actions && !navigations) return null

  return (
    <PositioningPortal
      isOpen={isToggled}
      onOpen={handleOpenMenu(true)}
      onShouldClose={handleOpenMenu(false)}
      portalContent={
        <div className={menuItemClasses} ref={toggledMenuRef}>
          {actions ? (
            <div className={sectionClasses}>
              <div className="section-title">{menuTitle}</div>
              {actions.map(({ action, className, label, type = 'default' }) => (
                <div className={classNames('menu-item', className)} key={label}>
                  <LinkButton
                    backgroundColor="light"
                    className="menu-item__link"
                    label={label}
                    onClick={handleAction(action)}
                    type={type}
                  />
                </div>
              ))}
            </div>
          ) : null}
          {navigations ? (
            <div className={sectionClassname}>
              <div className="section-title">NAVIGATION</div>
              {navigations.map(({ action, className, label, type = 'default' }) => (
                <div className={classNames('menu-item', className)} key={label}>
                  <LinkButton
                    backgroundColor="light"
                    className="menu-item__link"
                    label={label}
                    onClick={handleAction(action)}
                    type={type}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      }
    >
      <div className={menuContainerClasses} onClick={handleClick}>
        {mainAction ? (
          <>
            {mainAction.tooltipOverlay ? (
              <Tooltip
                className="actions-menu-tooltip"
                overlay={mainAction?.tooltipOverlay}
                placement="top"
                trigger="hover"
              >
                <div className={mainActionClasses} onClick={mainAction.action}>
                  <img className="icon" src={mainAction.icon} />
                </div>
              </Tooltip>
            ) : (
              <div className={mainActionClasses} onClick={mainAction.action}>
                <img className="icon" src={mainAction.icon} />
              </div>
            )}
          </>
        ) : null}
        {actions || navigations ? (
          disabled ? (
            <UnauthorizedTooltip>
              <div className={dropdownMenuClasses} onClick={handleOpenMenu(false)} ref={menuRef}>
                {iconDirection === 'horizontal' ? <BsThreeDots size={18} /> : <BsThreeDotsVertical size={18} />}
              </div>
            </UnauthorizedTooltip>
          ) : (
            <div className={dropdownMenuClasses} onClick={handleOpenMenu(true)} ref={menuRef}>
              {iconDirection === 'horizontal' ? <BsThreeDots size={18} /> : <BsThreeDotsVertical size={18} />}
            </div>
          )
        ) : null}
      </div>
    </PositioningPortal>
  )
}

export default ActionMenu
